import { RequirePayoutsAccountingAdminUser } from '@/components/auth/RequirePayoutsAccountingAdminUser';
import { RequirePayoutsAccountingApproverUser } from '@/components/auth/RequirePayoutsAccountingApproverUser';
import { AsyncSelectInputOption } from '@/components/Forms/AsyncSelect';
import { AddToBatchTargetModal } from '@/components/Payouts/AddToBatchTargetModal';
import { AddPayeeTransactionModal } from '@/components/Payouts/admin/AddPayeeTransactionModal';
import { AddTransactionModal } from '@/components/Payouts/admin/AddTransactionModal';
import { AddTransactionToBatchModal } from '@/components/Payouts/admin/AddTransactionToBatchModal';
import ApproveBatchConfirmationModal from '@/components/Payouts/admin/ApproveBatchConfirmationModal';
import { RecalculateBatchesModal } from '@/components/Payouts/admin/RecalculateBatchesModal';
import { RemoveTransactionsConfirmationModal } from '@/components/Payouts/admin/RemoveTransactionsConfirmationModal';
import { UnpauseTransactionOnlyModal } from '@/components/Payouts/admin/UnpauseTransactionOnlyModal';
import getConfigValue from '@/config';
import { buildQueryString } from '@/helpers/buildQueryString';
import { isErrorWithData, isErrorWithErrors } from '@/services/helpers';
import {
  useAddTransactionToBatchMutation,
  useApproveBatchesMutation,
  useCreateProjectTransactionMutation,
  useUnbatchTransactionMutation,
  useUpdateProjectTransactionStatusMutation,
} from '@/services/payouts';
import { PayoutTransactionClientData } from '@/types/Payouts';
import { Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CreateProjectTransactionPayload,
  IsoDateString,
  PayoutTransactionLevel,
  ProjectTransactionStatus,
} from 'types';

export type ProjectTransactionButton =
  | 'addToBatch'
  | 'addTransaction'
  | 'unpause'
  | 'export'
  | 'unbatch'
  | 'approve'
  | 'recalculate';

export interface NegativePayeeTotal {
  payeeId: string;
  payeeName: string;
  total: number;
  batchId: string;
}

interface ProjectTransactionsButtonBarProps {
  buttons?: ProjectTransactionButton[];
  selectedIds?: string[];
  resetSelections?(): void;
  query?: any;
  transactions?: PayoutTransactionClientData[];
  payeesWithNegativeTotals?: NegativePayeeTotal[];
  transactionLevel?: PayoutTransactionLevel;
  /** Prefills the organization when adding a payee-level transaction. */
  defaultPayee?: AsyncSelectInputOption;
}

export const ProjectTransactionsButtonBar: React.FC<ProjectTransactionsButtonBarProps> = ({
  buttons,
  selectedIds,
  resetSelections,
  query,
  transactions,
  payeesWithNegativeTotals = [],
  transactionLevel,
  defaultPayee,
}) => {
  const api = getConfigValue('VITE_APP_API_BASE_URL');
  const { id } = useParams<{ id: string }>();
  const [addTransactionToBatch, { isLoading }] = useAddTransactionToBatchMutation();
  const [createTransaction] = useCreateProjectTransactionMutation();
  const [unbatchTransaction] = useUnbatchTransactionMutation();
  const [approveBatches, { isLoading: isApprovingBatch }] = useApproveBatchesMutation();
  const [updateProjectTransactionStatus, { isLoading: isLoadingUpdateProjectTransactionStatus }] =
    useUpdateProjectTransactionStatusMutation();
  const { batchDate } = useParams<{ batchDate: IsoDateString }>();
  const navigate = useNavigate();
  const { enableAdhocBatches = false } = useFlags();

  const {
    isOpen: isAddToBatchModalOpen,
    handleOpen: handleOpenAddToBatchModal,
    handleClose: handleCloseAddProjectTransactionToBatchModal,
  } = useOpenClose();

  const {
    isOpen: isAddTransactionModalOpen,
    handleOpen: handleOpenAddProjectTransactionModal,
    handleClose: handleCloseAddProjectTransactionModal,
  } = useOpenClose();

  const {
    isOpen: isConfirmRemoveTransactionModalOpen,
    handleOpen: handleOpenConfirmRemoveTransaction,
    handleClose: handleCloseConfirmRemoveTransaction,
  } = useOpenClose();
  const {
    isOpen: isUnpauseTransactionOnlyModalOpen,
    handleOpen: handleOpenUnpauseTransactionOnlyModal,
    handleClose: handleCloseUnpauseTransactionOnlyModal,
  } = useOpenClose();
  const {
    isOpen: isApproveModalOpen,
    handleOpen: handleOpenApproveModal,
    handleClose: handleCloseApproveModal,
  } = useOpenClose();
  const {
    isOpen: isRecalculateBatchOpen,
    handleOpen: handleRecalculateBatchModal,
    handleClose: handleCloseRecalculateBatchModal,
  } = useOpenClose();
  const handleAddTransactionToBatch = useCallback(async () => {
    try {
      if (!selectedIds?.length) {
        return;
      }
      await addTransactionToBatch({ transactionIds: selectedIds }).unwrap();
      toast.success('Transaction added successfully');
      handleCloseAddProjectTransactionToBatchModal();
      if (resetSelections) {
        resetSelections();
      }
    } catch (error) {
      console.error(error);
      if (isErrorWithErrors(error)) {
        const errorMessage = error.data.message;
        toast.error(errorMessage);
      } else if (isErrorWithData(error)) {
        const errorMessage = error.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Could not add transaction(s) to batch');
      }
    }
  }, [selectedIds, addTransactionToBatch, handleCloseAddProjectTransactionToBatchModal, resetSelections]);

  const handleAddTransaction = useCallback(
    async (values: CreateProjectTransactionPayload) => {
      try {
        await createTransaction({
          ...values,
          ...(values.clawbackRuleName
            ? { description: `${values.clawbackRuleName} - ${values.description ?? ''}` }
            : {}),
        }).unwrap();
        handleCloseAddProjectTransactionModal();
        toast.success('Transaction added successfully');
      } catch (error) {
        console.error(error);
        toast.error('Could not add transaction');
      }
    },
    [createTransaction, handleCloseAddProjectTransactionModal],
  );

  const handleUnpauseTransactionOnlyModal = useCallback(async () => {
    try {
      if (!selectedIds?.length) return;

      for (const transactionId of selectedIds) {
        await updateProjectTransactionStatus({
          transactionId,
          status: ProjectTransactionStatus.open,
        }).unwrap();
      }

      toast.success('Transaction unpause successfully');
      handleCloseUnpauseTransactionOnlyModal();
      if (resetSelections) {
        resetSelections();
      }
    } catch (error) {
      toast.error('Could not unpause transaction(s)');
      console.error(error);
    }
  }, [selectedIds, updateProjectTransactionStatus, handleCloseUnpauseTransactionOnlyModal, resetSelections]);

  const unbatchSelectedTransactions = useCallback(async () => {
    if (!selectedIds?.length) return;
    try {
      for (const id of selectedIds) {
        try {
          await unbatchTransaction({ transactionId: id }).unwrap();
        } catch (error) {
          // This list is served from a materialized view that lags the write, so a row can still
          // look batched right after a successful unbatch. Failing the whole selection on 409 left
          // people retrying a transaction that was already out of its batch.
          if ((error as FetchBaseQueryError)?.status !== 409) throw error;
        }
      }
      handleCloseConfirmRemoveTransaction();
      if (resetSelections) {
        resetSelections();
      }
      toast.success('Transactions removed successfully');
    } catch (error) {
      console.error(error);
      toast.error('Could not remove transactions');
    }
  }, [selectedIds, unbatchTransaction, handleCloseConfirmRemoveTransaction, resetSelections]);

  const approveBatch = useCallback(async () => {
    try {
      if (isApprovingBatch) return;
      const batchIds: string[] = [
        // this should not need casting, but here we are
        ...new Set(transactions?.map(({ batchId }) => batchId as unknown as string).filter(Boolean)),
      ];
      if (!batchIds?.length) return;
      await approveBatches({
        batchIds,
      }).unwrap();
      handleCloseApproveModal();
      toast.success('Batch approved successfully');
      navigate(`/admin/payouts/queued`);
    } catch (error) {
      console.error(error);
      toast.error('Could not approve batch');
    }
  }, [approveBatches, transactions, isApprovingBatch, navigate, handleCloseApproveModal]);

  const transactionTotalAmount = useMemo(() => {
    return transactions?.reduce((acc, transaction) => acc + transaction.amount, 0) ?? 0;
  }, [transactions]);

  // One name for the action everywhere. Accounting already knows this wording from the Unbatched,
  // All and Paused pages, so the batch detail page must not invent a second name for it.
  const addToBatchLabel = useMemo(
    () => `Add transaction${selectedIds && selectedIds.length > 1 ? 's' : ''} to Batch`,
    [selectedIds],
  );

  return buttons?.length ? (
    <Box>
      <Box>
        {enableAdhocBatches ? (
          // Mounted only while open so it snapshots the selection at open time.
          isAddToBatchModalOpen && (
            <AddToBatchTargetModal
              isOpen={isAddToBatchModalOpen}
              handleClose={handleCloseAddProjectTransactionToBatchModal}
              selectedIds={selectedIds}
              transactions={transactions}
              onSuccess={resetSelections}
            />
          )
        ) : (
          <AddTransactionToBatchModal
            isOpen={isAddToBatchModalOpen}
            handleClose={handleCloseAddProjectTransactionToBatchModal}
            modalTitle="Add Transaction to Batch"
            submitButtonText="Add To Batch"
            modalId="addTransactionModal"
            handleSubmit={handleAddTransactionToBatch}
            isAddingBatchToTransaction={isLoading}
          />
        )}
        <RemoveTransactionsConfirmationModal
          isOpen={isConfirmRemoveTransactionModalOpen}
          onClose={handleCloseConfirmRemoveTransaction}
          onConfirm={unbatchSelectedTransactions}
          count={selectedIds?.length || 0}
          total={
            selectedIds?.reduce((acc, id) => {
              const transaction = transactions?.find((transaction) => transaction.id === id);
              return acc + (transaction?.amount ?? 0);
            }, 0) ?? 0
          }
        />

        <UnpauseTransactionOnlyModal
          isOpen={isUnpauseTransactionOnlyModalOpen}
          handleClose={handleCloseUnpauseTransactionOnlyModal}
          modalTitle="Unpause Transaction"
          submitButtonText="Confirm"
          modalId="UnpauseTransactionModal"
          handleSubmit={handleUnpauseTransactionOnlyModal}
          isAddingBatchToTransaction={isLoadingUpdateProjectTransactionStatus}
        />
        {transactionLevel === PayoutTransactionLevel.payee ? (
          <AddPayeeTransactionModal
            isOpen={isAddTransactionModalOpen}
            handleClose={handleCloseAddProjectTransactionModal}
            modalId="addPayeeTransactionModal"
            defaultPayee={defaultPayee}
          />
        ) : (
          <AddTransactionModal
            isOpen={isAddTransactionModalOpen}
            handleClose={handleCloseAddProjectTransactionModal}
            modalTitle="Add Payout"
            submitButtonText="Add Payout"
            modalId="addPaymentModal"
            handleSubmit={handleAddTransaction}
            accountId={id ?? undefined}
            hideAccountSelection={Boolean(id)}
            allowHistoricalAdjustment={Boolean(id)}
            batchDate={batchDate}
            showFincoEPCOption
          />
        )}
        <RecalculateBatchesModal
          isOpen={isRecalculateBatchOpen}
          payeesWithNegativeTotals={payeesWithNegativeTotals}
          onClose={handleCloseRecalculateBatchModal}
        />
        <ApproveBatchConfirmationModal
          isOpen={isApproveModalOpen}
          onConfirm={approveBatch}
          onClose={handleCloseApproveModal}
          amount={transactionTotalAmount}
          batchDate={batchDate}
        />
      </Box>

      <Box direction="row-reverse" alignItems="center" padding="sm lg" gap="sm">
        {
          // This is the only button which is available to organization users
          buttons.includes('export') && query && (
            <Button
              as="a"
              size="sm"
              variant="primary"
              tone="primary"
              iconPrefix="download"
              href={`${api}/payouts/transactions?${buildQueryString({ ...query, format: 'csv' })}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Export
            </Button>
          )
        }
        <RequirePayoutsAccountingAdminUser>
          {buttons.includes('addTransaction') ? (
            <Button
              iconPrefix="add"
              size="sm"
              variant="secondary"
              tone="neutral"
              disabled={isLoading}
              onClick={handleOpenAddProjectTransactionModal}
            >
              Add Transaction
            </Button>
          ) : null}
          {buttons.includes('unpause') && selectedIds && selectedIds.length ? (
            <Button size="sm" iconPrefix="play" variant="primary" onClick={handleOpenUnpauseTransactionOnlyModal}>
              Unpause transaction{selectedIds && selectedIds.length > 1 ? 's' : ''} Only
            </Button>
          ) : null}
          {buttons.includes('addToBatch') ? (
            <Button
              size="sm"
              iconPrefix="add"
              variant="primary"
              onClick={handleOpenAddToBatchModal}
              isDisabled={!enableAdhocBatches && !selectedIds?.length}
            >
              {addToBatchLabel}
            </Button>
          ) : null}
          {buttons.includes('unbatch') && selectedIds?.length ? (
            <Box childGap="sm">
              <Button
                size="sm"
                iconPrefix="trash"
                variant="primary"
                tone="danger"
                onClick={handleOpenConfirmRemoveTransaction}
              >
                Remove Transaction{selectedIds.length > 1 ? 's' : ''}
              </Button>
            </Box>
          ) : null}
          {buttons.includes('recalculate') ? (
            <Button
              iconPrefix="tools"
              size="sm"
              variant="secondary"
              tone="neutral"
              onClick={handleRecalculateBatchModal}
              disabled={isLoading}
            >
              Recalculate Batch
            </Button>
          ) : null}
          {buttons.includes('approve') && !payeesWithNegativeTotals.length ? (
            <RequirePayoutsAccountingApproverUser>
              <Button
                iconPrefix="check"
                size="sm"
                variant="primary"
                onClick={handleOpenApproveModal}
                disabled={isLoading || payeesWithNegativeTotals.length > 0}
              >
                Approve
              </Button>
            </RequirePayoutsAccountingApproverUser>
          ) : null}
        </RequirePayoutsAccountingAdminUser>
        {/*<Box>{toolbar}</Box>*/}
      </Box>
    </Box>
  ) : null;
};
